import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { environment } from '../../../../../../environments/environment';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { FileDropzoneComponent } from '../../../../../shared/components/file-dropzone/file-dropzone.component';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { TimelineHistoricoComponent, TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { TipoContatoEnum } from '../../../../../shared/enums/pessoa/tipo-contato.enum';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { OrigemOcorrenciaEnum } from '@shared/enums/vendas/origem-ocorrencia.enum';
import { TipoOcorrenciaOportunidadeEnum } from '@shared/enums/vendas/tipo-ocorrencia-oportunidade.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { VendaDaOportunidadeStruct } from '@shared/services/structs/vendas/venda-da-oportunidade.struct';
import { VendaOcorrenciaOportunidadeStruct } from '@shared/services/structs/vendas/venda-ocorrencia-oportunidade.struct';
import { NotaFiscalSaidaDoDestinatarioStruct } from '@shared/services/structs/repo-dfe/nota-fiscal-saida-do-destinatario.struct';
import { GetHistoricoClienteOportunidadeResponse } from '@shared/services/responses/vendas/get-historico-cliente-oportunidade.response';
import { GetFinanceiroOportunidadeResponse } from '@shared/services/responses/vendas/get-financeiro-oportunidade.response';
import { TituloClienteStruct } from '@shared/services/structs/financas/titulo-cliente.struct';
import { StatusContasReceberEnum } from '@shared/enums/financas/status-contas-receber.enum';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { classeBadgeStatusNFeSaida } from '@shared/helpers/nota-fiscal-saida-status.helper';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { OportunidadeOcorrenciaStruct } from '@shared/services/structs/vendas/oportunidade-ocorrencia.struct';
import { CriarOportunidadeOcorrenciaRequest } from '@shared/services/requests/vendas/criar-oportunidade-ocorrencia.request';
import { OportunidadeOcorrenciaEditarRequest } from '@shared/services/requests/vendas/oportunidade-ocorrencia-editar.request';
import { ReturnStruct } from '@shared/services/structs/return.struct';
import { MaskService } from '@shared/services/mask.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { OportunidadeService } from '../../../../../shared/services/apis/vendas/oportunidade.service';
import { PessoaService } from '../../../../../shared/services/apis/pessoa/pessoa.service';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ContatoPessoaStruct } from '../../../../../shared/services/structs/pessoa/contato-pessoa.struct';
import { VendedorLookupStruct } from '@shared/services/structs/pessoa/vendedor-lookup.struct';
import { EnvolvidosEtapaService } from '../../envolvidos-etapa.service';
import { corDoNome, dataDaApi, iniciaisDoNome, nomeDoVendedor, origemDoValor } from '../../crm-oportunidades.mapper';
import {
  LookupOportunidade,
  OportunidadeAnexo,
  OportunidadeCampoValorItem,
  OportunidadeComentario,
} from '../../../../../shared/services/models/vendas/oportunidade.model';
import { CustomDateAdapter } from '../../../../../shared/services/custom-date-adapter';
import { VendaDocumentoService } from '@shared/services/apis/vendas/venda-documento.service';
import { KanbanCor } from '@shared/services/models/vendas/kanban-cor.model';
import { PvCorVm, resolverCor } from '@shared/helpers/kanban-cor.helper';
import {
  AuditChange,
  OportunidadeCard,
  OportunidadeStage,
  Person,
  PRIORIDADE_POR_CHAVE,
  PRIORIDADES,
  PrioridadeChave,
  PrioridadeInfo,
  ID_POR_PRIORIDADE,
  STATUS_CLIENTE_INDEFINIDO,
  StatusClienteChave,
  StatusClienteInfo,
  TIPO_CAMPO,
} from '../../crm-oportunidades.models';
import {
  EncerrarOportunidadeDialogComponent,
  EncerrarOportunidadeResult,
} from '../encerrar-oportunidade-dialog/encerrar-oportunidade-dialog.component';
import {
  AcaoFichaCard,
  CampoFicha,
  FormularioTimeline,
  GrupoEtapaFicha,
  HistoricoOportunidadeItem,
  LinhaDoTempoFicha,
  OutroAssuntoFicha,
  OportunidadeCardDialogData,
  OportunidadeCardDialogResult,
} from './oportunidade-card-dialog.types';

type PainelFicha = 'tags' | 'outros' | 'anexos' | 'comentarios' | 'historico';
type AbaFicha = 'etapa' | 'timeline' | 'vendas' | 'financeiro' | 'servicos';

/**
 * Lançamentos da linha do tempo: ações, e-mails, anotações, orçamentos, encerramento e reabertura. Os demais
 * tipos são eventos do sistema e ficam só no Histórico da lateral.
 */
const TIPOS_LINHA_DO_TEMPO: TipoOcorrenciaOportunidadeEnum[] = [
  TipoOcorrenciaOportunidadeEnum.AcaoPendente,
  TipoOcorrenciaOportunidadeEnum.AcaoConcluida,
  TipoOcorrenciaOportunidadeEnum.EnvioEmail,
  TipoOcorrenciaOportunidadeEnum.Anotacao,
  TipoOcorrenciaOportunidadeEnum.Orcamento,
  TipoOcorrenciaOportunidadeEnum.OrcamentoFormalizado,
  TipoOcorrenciaOportunidadeEnum.Encerramento,
  TipoOcorrenciaOportunidadeEnum.Reabertura,
];

/** Ícone e cores do bloco de cada tipo na linha do tempo (protótipo). */
const ESTILOS_LINHA_DO_TEMPO: Record<number, { icone: string; cor: string; fundo: string }> = {
  [TipoOcorrenciaOportunidadeEnum.AcaoPendente]: { icone: 'schedule', cor: '#786704', fundo: '#FDFDEC' },
  [TipoOcorrenciaOportunidadeEnum.AcaoConcluida]: { icone: 'check_circle', cor: '#1E8E3E', fundo: '#E6F4EA' },
  [TipoOcorrenciaOportunidadeEnum.EnvioEmail]: { icone: 'mail', cor: '#1E5DB8', fundo: '#E8F1FF' },
  [TipoOcorrenciaOportunidadeEnum.Anotacao]: { icone: 'sticky_note_2', cor: '#786704', fundo: '#FDFDEC' },
  [TipoOcorrenciaOportunidadeEnum.Orcamento]: { icone: 'request_quote', cor: '#786704', fundo: '#FDFDEC' },
  [TipoOcorrenciaOportunidadeEnum.OrcamentoFormalizado]: { icone: 'request_quote', cor: '#786704', fundo: '#FDFDEC' },
  [TipoOcorrenciaOportunidadeEnum.Encerramento]: { icone: 'flag', cor: '#B3261E', fundo: '#FCEEEE' },
  [TipoOcorrenciaOportunidadeEnum.Reabertura]: { icone: 'restart_alt', cor: '#1E5DB8', fundo: '#E8F1FF' },
};

const MESES_ABREVIADOS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const MENSAGEM_CRIAR_DOCUMENTO_ENCERRADA = 'Reabra a oportunidade para criar orçamento ou pedido.';
const MENSAGEM_CRIAR_DOCUMENTO_SEM_PERMISSAO = 'Sem permissão para incluir no Processo de Venda.';
const MENSAGEM_VINCULAR_ENCERRADA = 'Oportunidade encerrada: reabra para vincular.';
const MENSAGEM_VINCULAR_ARQUIVADA = 'Oportunidade arquivada: desarquive para vincular.';

/** A API grava a ação com prefixo e ponto final ("Próxima ação definida: X."); a linha mostra só o texto dela. */
const PREFIXO_ACAO = /^(?:Próxima ação definida|Ação concluída):\s*(.+?)\.?$/s;


@Component({
  selector: 'app-oportunidade-card-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AbntPipe,
    AlertCardComponent,
    LoadingBoxComponent,
    FileDropzoneComponent,
    SelectPesquisavelComponent,
    EmptyStateComponent,
    TimelineHistoricoComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './oportunidade-card-dialog.component.html',
  styleUrl: './oportunidade-card-dialog.component.css',
})
export class OportunidadeCardDialogComponent implements OnInit, OnDestroy {
  readonly card: OportunidadeCard;
  readonly stages: OportunidadeStage[];
  readonly statusOpcoes: StatusClienteInfo[];
  readonly prioridades = PRIORIDADES;
  /** Campo configurável do tipo Número. */
  readonly mascaraNumero: ReturnType<MaskService['getDecimalMask']>;
  /** Valor da oportunidade e valor estimado. */
  readonly mascaraMoeda: ReturnType<MaskService['getCurrencyMask']> & { allowNegative: boolean };

  statusCliente: StatusClienteChave;


  private camposPorEtapa: Record<string, OportunidadeCampoValorItem[]> = {};

  /** Abre na etapa atual; encerrada (etapa final), na linha do tempo. */
  aba: AbaFicha;
  painel: PainelFicha | null = null;
  moverAberto = false;

  isExpanded = false;

  /** Histórico completo, do GET .../historico. Comentário é conversa do card e não entra aqui. */
  historico: HistoricoOportunidadeItem[] = [];
  /** Registros do CRM na aba "linha do tempo": ações, e-mails, anotações e orçamentos. */
  historicoTimeline: HistoricoOportunidadeItem[] = [];
  /** Aba "linha do tempo": os registros do CRM e um por documento de Vendas vinculado, por data desc. */
  linhasDoTempo: LinhaDoTempoFicha[] = [];
  /** Histórico da lateral: eventos do sistema do CRM e ocorrências dos documentos de Vendas, só leitura. */
  historicoSistema: TimelineHistoricoItem[] = [];
  carregandoHistorico = true;
  historicoSelecionado: HistoricoOportunidadeItem | null = null;
  /** Anotar e editar registros da linha do tempo exigem permissão de alterar a oportunidade. */
  readonly podeAlterar: boolean;
  /** Excluir registro da linha do tempo exige a permissão de excluir do CRM, a mesma da API. */
  readonly podeExcluirRegistro: boolean;
  /** O status é do cadastro do cliente: trocar exige Alterar do CRM e Alterar Pessoas. */
  readonly motivoStatusSemPermissao: string | null;
  itemExcluindo: HistoricoOportunidadeItem | null = null;
  /** Linha da linha do tempo com a confirmação de exclusão aberta. */
  registroConfirmandoExclusao: HistoricoOportunidadeItem | null = null;
  readonly estilosLinhaDoTempo = ESTILOS_LINHA_DO_TEMPO;
  /** Documento de Vendas na linha do tempo: as cores do registro de orçamento; o ícone muda por tipo. */
  readonly estiloDocumentoLinhaDoTempo = ESTILOS_LINHA_DO_TEMPO[TipoOcorrenciaOportunidadeEnum.Orcamento];

  tituloEditando = false;
  tituloDraft = '';
  /** Troca de favorito em andamento: a estrela fica desabilitada até a resposta. */
  isFavoritando = false;

  /** Formulário aberto no topo da linha do tempo; abrir outro fecha este sem salvar. */
  formularioTimeline: FormularioTimeline | null = null;
  /** Registro da linha do tempo aberto no formulário (edição, ou e-mail a reenviar). */
  registroEditando: HistoricoOportunidadeItem | null = null;
  salvandoFormulario = false;
  @ViewChild('areaPrincipal') private areaPrincipal?: ElementRef<HTMLElement>;
  @ViewChild('primeiroCampoTimeline') private primeiroCampoTimeline?: ElementRef<HTMLElement>;

  acaoTexto = '';
  acaoComData = true;
  acaoData: Date | null = null;
  acaoResponsavel: number | null = null;

  emailPara = '';
  emailAssunto = '';
  emailMensagem = '';

  anotacaoTexto = '';
  acaoConcluidaTexto = '';
  readonly tamanhoMaximoRegistro = 1000;

  /** Tags marcadas no painel: o select múltiplo mostra as escolhidas como selos logo abaixo. */
  tagsControl = new FormControl<number[]>([], { nonNullable: true });

  anexos: OportunidadeAnexo[] = [];
  comentarios: OportunidadeComentario[] = [];
  comentarioTexto = '';
  comentarioEmEdicao: number | null = null;
  comentarioDraft = '';
  enviandoAnexo = false;

  /** Aba Vendas: documentos vinculados (GET .../documentos), só com Visualizar do Processo de Venda. */
  readonly podeVisualizarVendas: boolean;
  private readonly podeInserirVendas: boolean;
  documentos: VendaDaOportunidadeStruct[] = [];
  carregandoDocumentos = false;
  /** Na recarga (depois de vincular ou desvincular) a tabela continua na tela, sem skeleton. */
  documentosCarregados = false;
  readonly colunasDocumentos = ['numero', 'tipo', 'data', 'vendedor', 'valor', 'situacao', 'acoes'];
  readonly mensagemClienteBloqueado =
    'Esta oportunidade tem documento de Vendas vinculado. Para outro cliente, crie uma nova oportunidade.';

  /** Vincular e desvincular exigem Alterar do CRM e Alterar do Processo de Venda; null quando pode. */
  readonly motivoVinculoSemPermissao: string | null;
  /** Documento com a confirmação de desvínculo aberta (aba Vendas e linha do tempo). */
  idVendaConfirmandoDesvinculo: number | null = null;
  /** Documento sendo vinculado ou desvinculado: o spinner fica só no botão dele, e as demais ações esperam. */
  idVendaProcessando: number | null = null;

  /** Aba Vendas: histórico do cliente (GET .../historico-cliente), uma vez por abertura da aba. */
  historicoCliente: GetHistoricoClienteOportunidadeResponse | null = null;
  carregandoHistoricoCliente = false;
  readonly colunasPedidosCliente = ['numero', 'data', 'vendedor', 'valor', 'situacao'];
  readonly colunasNotasCliente = ['numero', 'data', 'valor', 'situacao'];
  readonly colunasOutrosDocumentos = ['numero', 'tipo', 'data', 'vendedor', 'valor', 'situacao', 'acoes'];

  /** Aba Financeiro (GET .../financeiro), só para consulta e só com Visualizar de Contas a Receber. */
  readonly podeVisualizarFinanceiro: boolean;
  financeiro: GetFinanceiroOportunidadeResponse | null = null;
  carregandoFinanceiro = false;
  /** Botão que disparou o recarregamento do financeiro: só ele mostra o spinner. */
  acaoFinanceiro: 'pagosRecentes' | 'tentarNovamente' | null = null;
  /** Falha da requisição inteira (isError ou HTTP): o alerta da aba com "Tentar novamente". */
  erroFinanceiro: string | null = null;
  /** Filtro "Incluir pagos recentes": recarrega a lista; os totais vêm do servidor e não mudam. */
  incluirPagosRecentes = false;
  readonly colunasTitulos = ['data', 'valor', 'vencimento', 'documento', 'situacao'];
  readonly colunasParcelasPedido = ['parcela', 'vencimento', 'valor'];
  readonly statusContasReceber = StatusContasReceberEnum;

  /** Predicados das linhas da tabela de documentos (multiTemplateDataRows). */
  readonly isDocumentoComAcesso = (_indice: number, documento: VendaDaOportunidadeStruct): boolean =>
    !documento.isSemAcesso;
  readonly isDocumentoSemAcesso = (_indice: number, documento: VendaDaOportunidadeStruct): boolean =>
    documento.isSemAcesso;
  readonly isConfirmandoDesvinculo = (_indice: number, documento: VendaDaOportunidadeStruct): boolean =>
    !documento.isSemAcesso && documento.idVenda === this.idVendaConfirmandoDesvinculo;

  /** Troca de vendedor com orçamentos em aberto: a pergunta inline abaixo do campo, até Sim, Não ou Cancelar. */
  trocaVendedor: { idVendedor: number; nome: string; quantidade: number } | null = null;
  consultandoOrcamentosEmAberto = false;
  salvandoVendedor = false;
  /** Resposta da pergunta da troca de vendedor em gravação: só o botão clicado mostra o spinner. */
  respostaTrocaVendedor: boolean | null = null;
  @ViewChild('botaoSimTrocaVendedor') private botaoSimTrocaVendedor?: ElementRef<HTMLButtonElement>;

  private houveMudanca = false;
  private etapaOriginal: string;

  
  private camposSujos = new Set<number>();
  private campoAlterado$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  
  private camposPorId = new Map<number, CampoFicha>();

  
  readonly origens: LookupOportunidade[];
  readonly canais: LookupOportunidade[];

  
  readonly urlPessoa = `${environment.urlApiPessoa}Lookup/listPessoaSelect`;
  readonly filtroCliente = { idTipoContato: TipoContatoEnum.Cliente };

  clienteControl = new FormControl<number | null>(null);
  contatoControl = new FormControl<number | null>(null);
  vendedorControl = new FormControl<number | null>(null);
  /** Troca de vendedor: ativos dentro do escopo, mais o atual mesmo inativo (RV-06). */
  opcoesVendedor: VendedorLookupStruct[] = [];
  private readonly vendedoresPorId: Map<number, VendedorLookupStruct>;

  /** "Criado por" (CA-16): data e hora da inclusão, no fuso de quem olha. */
  readonly dataCriacao: string;

  
  contatos: ContatoPessoaStruct[] = [];
  carregandoContatos = false;

  idOrigemLead: number;
  idCanalContato: number | null;
  valorEstimado: number | null;
  private valorEstimadoSujo = false;
  private valorEstimadoAlterado$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<OportunidadeCardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OportunidadeCardDialogData,
    private dialog: MatDialog,
    private alertService: AlertService,
    private oportunidadeService: OportunidadeService,
    private pessoaService: PessoaService,
    private pessoaLookupService: PessoaLookupService,
    private envolvidosEtapaService: EnvolvidosEtapaService,
    private authService: AuthService,
    private router: Router,
    private vendaDocumentoService: VendaDocumentoService,
    private processoVendaService: ProcessoVendaService,
    maskService: MaskService
  ) {
    const permissoes = this.authService.getUserInfos()?.listaPermissoes ?? [];
    this.podeAlterar = permissoes.includes(PermissaoEnum.Vendas_Crm_Alterar);
    this.podeExcluirRegistro = permissoes.includes(PermissaoEnum.Vendas_Crm_Excluir);
    this.podeVisualizarVendas = permissoes.includes(PermissaoEnum.Vendas_ProcessoVenda_Visualizar);
    this.podeInserirVendas = permissoes.includes(PermissaoEnum.Vendas_ProcessoVenda_Inserir);
    this.podeVisualizarFinanceiro = permissoes.includes(PermissaoEnum.Financas_ContasReceber_Visualizar);
    const podeAlterarVendas = permissoes.includes(PermissaoEnum.Vendas_ProcessoVenda_Alterar);
    this.motivoVinculoSemPermissao = !this.podeAlterar && !podeAlterarVendas
      ? 'Sem permissão para alterar o CRM e o Processo de Venda.'
      : !this.podeAlterar
        ? 'Sem permissão para alterar o CRM.'
        : !podeAlterarVendas ? 'Sem permissão para alterar o Processo de Venda.' : null;
    this.motivoStatusSemPermissao = !this.podeAlterar
      ? 'Sem permissão para alterar o CRM.'
      : !permissoes.includes(PermissaoEnum.Cadastros_Pessoas_Alterar)
        ? 'Sem permissão para alterar o cadastro de pessoas.'
        : null;
    this.mascaraNumero = maskService.getDecimalMask(2);
    this.mascaraMoeda = { ...maskService.getCurrencyMask(), allowNegative: false };
    this.card = data.card;
    this.stages = data.stages;
    this.aba = this.etapaAtual?.isFinal ? 'timeline' : 'etapa';
    this.statusCliente = data.statusCliente;
    this.statusOpcoes = data.statusOpcoes ?? [];
    this.etapaOriginal = data.card.stage;
    this.tagsControl.setValue(data.card.tags.map(t => t.idTagCrm));
    this.anexos = data.anexos ?? [];
    this.comentarios = data.comentarios ?? [];
    this.vendedoresPorId = new Map((data.vendedores ?? []).map(v => [v.idVendedor, v]));
    // Já vem na hora da empresa (sem fuso): o pipe date exibe como recebido.
    this.dataCriacao = data.card.datetimeInclusao;
    this.montarOpcoesVendedor();

    this.origens = data.origens ?? [];
    this.canais = data.canais ?? [];
    this.clienteControl.setValue(data.card.idCliente, { emitEvent: false });
    this.contatoControl.setValue(data.card.idContato, { emitEvent: false });
    this.vendedorControl.setValue(data.card.idVendedorResponsavel, { emitEvent: false });
    this.idOrigemLead = data.card.idOrigemLead;
    this.idCanalContato = data.card.idCanalContato;
    this.valorEstimado = data.card.valorEstimado;

    if (this.prospeccaoAtual) this.carregarContatos(data.card.idCliente);

    this.clienteControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(idPessoaCliente => {
      this.contatoControl.setValue(null, { emitEvent: false });
      this.contatos = [];
      if (idPessoaCliente != null) this.carregarContatos(idPessoaCliente);
    });
  }

  ngOnInit(): void {
    this.carregarCampos();
    this.carregarHistorico();
    // Já na abertura: o bloqueio do cliente depende de haver documento vinculado, não só do que define o valor.
    this.carregarDocumentos();
    if (this.data.editarTitulo) this.iniciarEdicaoTitulo();

    this.campoAlterado$
      .pipe(debounceTime(600), takeUntil(this.destroy$))
      .subscribe(() => this.persistirCampos());

    this.valorEstimadoAlterado$
      .pipe(debounceTime(600), takeUntil(this.destroy$))
      .subscribe(() => this.persistirValorEstimado());
  }

  ngOnDestroy(): void {
    this.persistirCampos();
    this.persistirValorEstimado();
    this.destroy$.next();
    this.destroy$.complete();
  }

  
  get prospeccaoAtual(): boolean {
    return this.indiceEtapaAtual === 0;
  }


  /** Todas as etapas: a movimentação na própria ficha leva a etapas que não eram a atual na abertura. */
  private carregarCampos(): void {
    const porEtapa = new Map<number, OportunidadeCampoValorItem[]>();
    for (const campo of this.data.campos ?? []) {
      const lista = porEtapa.get(campo.idEtapaKanban);
      if (lista) lista.push(campo);
      else porEtapa.set(campo.idEtapaKanban, [campo]);
    }

    for (const stage of this.stages) {
      const campos = porEtapa.get(stage.idEtapaKanban);
      if (campos) this.camposPorEtapa[stage.key] = campos;
    }

    this.recalcularGruposEtapa();
  }

  get indiceEtapaAtual(): number {
    return Math.max(0, this.stages.findIndex(s => s.key === this.card.stage));
  }

  get etapaAtual(): OportunidadeStage | undefined {
    return this.stages.find(s => s.key === this.card.stage);
  }

  /** Aba "etapa atual": campos editáveis da etapa; null com a oportunidade encerrada. */
  grupoAtual: GrupoEtapaFicha | null = null;
  /** Lateral "Etapas anteriores", em leitura, da mais recente para a primeira. */
  gruposAnteriores: GrupoEtapaFicha[] = [];

  private recalcularGruposEtapa(): void {
    const indiceAtual = this.indiceEtapaAtual;
    const encerrada = !!this.etapaAtual?.isFinal;

    const montarGrupo = (indice: number): GrupoEtapaFicha => {
      const stage = this.stages[indice];
      // Na final, Resultado e Motivo de sistema ficam vazios: o encerramento grava na oportunidade, e o grupo mostra de lá.
      const campos = (this.camposPorEtapa[stage.key] ?? [])
        .filter(c => !(stage.isFinal && c.isSistemico))
        .map(c => this.mapCampo(c));
      campos.forEach(campo => this.camposPorId.set(campo.idCampoDefinicao, campo));
      return { stage, campos, isInicial: indice === 0 };
    };

    this.grupoAtual = encerrada ? null : montarGrupo(indiceAtual);

    // Encerrada: as etapas principais percorridas e a final em que parou viram anteriores.
    const anteriores: GrupoEtapaFicha[] = [];
    for (let i = encerrada ? indiceAtual : indiceAtual - 1; i >= 0; i--) {
      if (this.stages[i].isFinal && i !== indiceAtual) continue;

      const grupo = montarGrupo(i);
      if (grupo.campos.length || grupo.isInicial || grupo.stage.isFinal) anteriores.push(grupo);
    }

    this.gruposAnteriores = anteriores;
  }

  private mapCampo(campo: OportunidadeCampoValorItem): CampoFicha {
    const controle = this.controleDoTipo(campo.idTipoCampo);
    return {
      idCampoDefinicao: campo.idCampoDefinicao,
      nome: campo.nome,
      obrigatorio: campo.isObrigatorio,
      controle,
      opcoes: campo.opcoes ?? [],
      prefixo: campo.idTipoCampo === TIPO_CAMPO.moeda ? 'R$' : null,
      sufixo: campo.idTipoCampo === TIPO_CAMPO.percentual ? '%' : null,
    };
  }

  /** Moeda e percentual seguem como texto livre com prefixo/sufixo, como já gravados. */
  private controleDoTipo(idTipoCampo: number): CampoFicha['controle'] {
    switch (idTipoCampo) {
      case TIPO_CAMPO.textoLongo:
        return 'textoLongo';
      case TIPO_CAMPO.data:
        return 'data';
      case TIPO_CAMPO.numero:
        return 'numero';
      case TIPO_CAMPO.selecao:
        return 'selecao';
      case TIPO_CAMPO.selecaoMultipla:
        return 'selecaoMultipla';
      case TIPO_CAMPO.booleano:
        return 'booleano';
      case TIPO_CAMPO.anexo:
        return 'anexo';
      default:
        return 'texto';
    }
  }

  valorCampo(campo: CampoFicha): string {
    return this.card.camposValores[campo.idCampoDefinicao] ?? '';
  }

  
  definirValorCampo(campo: CampoFicha, valor: string): void {
    this.card.camposValores = { ...this.card.camposValores, [campo.idCampoDefinicao]: valor };
    this.houveMudanca = true;
    this.camposSujos.add(campo.idCampoDefinicao);
    this.campoAlterado$.next();
  }

  
  private persistirCampos(): void {
    if (!this.camposSujos.size) return;

    const ids = [...this.camposSujos];
    this.camposSujos.clear();

    const campos = ids.map(idCampoDefinicao => {
      const valor = this.card.camposValores[idCampoDefinicao] ?? '';
      const definicao = this.camposPorId.get(idCampoDefinicao);

      if (definicao?.controle === 'selecao' || definicao?.controle === 'selecaoMultipla') {
        return { idCampoDefinicao, idsOpcao: this.idsDoValor(valor) };
      }

      return { idCampoDefinicao, valor };
    });

    const devolverSujos = () => ids.forEach(id => this.camposSujos.add(id));

    this.oportunidadeService.salvarCampos(this.card.idOportunidade, { campos }).subscribe({
      next: response => {
        if (response.isError) {
          devolverSujos();
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível salvar os campos.', AlertType.warning);
          return;
        }
        this.recarregarHistorico();
      },
      error: () => {
        devolverSujos();
        this.alertService.show('Aviso',
          'Não foi possível salvar os campos agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  /** Seleção grava os ids das opções, separados por vírgula, em camposValores. */
  private idsDoValor(valor: string): number[] {
    return valor
      .split(',')
      .map(parte => parseInt(parte, 10))
      .filter(id => !Number.isNaN(id));
  }

  valorCampoSelecao(campo: CampoFicha): number | null {
    return this.idsDoValor(this.valorCampo(campo))[0] ?? null;
  }

  definirValorCampoSelecao(campo: CampoFicha, idCampoDefinicaoOpcao: number | null): void {
    this.definirValorCampo(campo, idCampoDefinicaoOpcao == null ? '' : String(idCampoDefinicaoOpcao));
  }

  valorCampoSelecaoMultipla(campo: CampoFicha): number[] {
    return this.idsDoValor(this.valorCampo(campo));
  }

  definirValorCampoSelecaoMultipla(campo: CampoFicha, idsOpcao: number[] | null): void {
    this.definirValorCampo(campo, (idsOpcao ?? []).join(','));
  }

  /** Número grava o valor invariante ("1234.5"), como os campos configuráveis do Kanban de Compras. */
  valorCampoNumero(campo: CampoFicha): number | null {
    const texto = this.valorCampo(campo);
    if (!texto) return null;
    const numero = Number(texto);
    return Number.isFinite(numero) ? numero : null;
  }

  definirValorCampoNumero(campo: CampoFicha, valor: number | null): void {
    this.definirValorCampo(campo, valor == null ? '' : String(valor));
  }

  /** Valor exibido nas etapas anteriores: opções pelo nome; o número é formatado no template pelo pipe abnt. */
  valorCampoLeitura(campo: CampoFicha): string {
    switch (campo.controle) {
      case 'selecao':
      case 'selecaoMultipla': {
        const ids = this.valorCampoSelecaoMultipla(campo);
        return campo.opcoes
          .filter(opcao => ids.includes(opcao.idCampoDefinicaoOpcao))
          .map(opcao => opcao.descricao)
          .join(', ');
      }
      default:
        return this.valorCampo(campo);
    }
  }

  valorCampoData(campo: CampoFicha): Date | null {
    return this.textoParaData(this.valorCampo(campo));
  }

  definirValorCampoData(campo: CampoFicha, data: Date | null): void {
    this.definirValorCampo(campo, data ? this.formatarData(data) : '');
  }

  campoLigado(campo: CampoFicha): boolean {
    return this.valorCampo(campo) === 'Sim';
  }

  alternarCampoBooleano(campo: CampoFicha, ligado: boolean): void {
    this.definirValorCampo(campo, ligado ? 'Sim' : 'Não');
  }

  private carregarContatos(idPessoa: number): void {
    this.carregandoContatos = true;

    this.pessoaLookupService.listContatoPessoaSelect(idPessoa).subscribe({
      next: response => {
        this.carregandoContatos = false;
        this.contatos = response.isError ? [] : response.list ?? [];
      },
      error: () => {
        this.carregandoContatos = false;
        this.contatos = [];
      },
    });
  }

  get semContatos(): boolean {
    return this.clienteControl.value != null && !this.carregandoContatos && !this.contatos.length;
  }

  
  onClienteSelecionado(selecionado: { id: number; descricao: string } | number | null): void {
    const pessoa = typeof selecionado === 'object' && selecionado ? selecionado : null;
    const idPessoaCliente = pessoa ? pessoa.id : (typeof selecionado === 'number' ? selecionado : null);
    if (idPessoaCliente == null || idPessoaCliente === this.card.idCliente) return;

    this.salvarCliente(idPessoaCliente, pessoa?.descricao ?? this.card.cliente);
  }

  
  private salvarCliente(idPessoaCliente: number, nomeCliente: string): void {
    const clienteAnterior = this.card.idCliente;
    const contatoAnterior = this.card.idContato;

    this.oportunidadeService
      .update(this.card.idOportunidade, { idPessoaCliente, idPessoaContato: null })
      .subscribe({
        next: response => {
          if (response.isError) {
            this.reverterCliente(clienteAnterior, contatoAnterior);
            this.alertService.show(
              'Atenção', response.errorDescription || 'Não foi possível trocar o cliente.', AlertType.warning);
            return;
          }
          this.card.idCliente = idPessoaCliente;
          this.card.cliente = nomeCliente;
          this.card.idContato = null;
          this.card.contato = null;
          this.card.contatoEmail = null;
          this.recarregarHistorico();

          // Histórico do cliente (aba Vendas) e financeiro são do cliente: com outro cliente, buscam de novo.
          this.historicoCliente = null;
          this.financeiro = null;
          this.erroFinanceiro = null;
          if (this.aba === 'financeiro') this.carregarFinanceiro(true);
        },
        error: () => {
          this.reverterCliente(clienteAnterior, contatoAnterior);
          this.alertService.show('Erro', 'Não foi possível trocar o cliente.', AlertType.error);
        },
      });
  }

  private reverterCliente(idCliente: number, idContato: number | null): void {
    this.clienteControl.setValue(idCliente, { emitEvent: false });
    this.contatoControl.setValue(idContato, { emitEvent: false });
    this.carregarContatos(idCliente);
  }

  onContatoSelecionado(selecionado: ContatoPessoaStruct | number | null): void {
    const contato = typeof selecionado === 'object' && selecionado ? selecionado : null;
    const idPessoaContato = contato ? contato.id : (typeof selecionado === 'number' ? selecionado : null);
    this.salvarContato(idPessoaContato, contato?.descricao ?? null, contato?.email ?? null);
  }

  private salvarContato(idPessoaContato: number | null, nomeContato: string | null, email: string | null): void {
    const anterior = this.card.idContato;

    this.oportunidadeService.update(this.card.idOportunidade, { idPessoaContato }).subscribe({
      next: response => {
        if (response.isError) {
          this.contatoControl.setValue(anterior, { emitEvent: false });
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível trocar o contato.', AlertType.warning);
          return;
        }
        this.card.idContato = idPessoaContato;
        this.card.contato = nomeContato;
        this.card.contatoEmail = email;
        this.recarregarHistorico();
      },
      error: () => {
        this.contatoControl.setValue(anterior, { emitEvent: false });
        this.alertService.show('Erro', 'Não foi possível trocar o contato.', AlertType.error);
      },
    });
  }

  alterarOrigemLead(idOrigemLead: number): void {
    const anterior = this.card.idOrigemLead;
    this.idOrigemLead = idOrigemLead;

    this.oportunidadeService.update(this.card.idOportunidade, { idOrigemLead }).subscribe({
      next: response => {
        if (response.isError) {
          this.idOrigemLead = anterior;
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível trocar a origem do lead.', AlertType.warning);
          return;
        }
        this.card.idOrigemLead = idOrigemLead;
        this.card.origem = this.origemNome(idOrigemLead);
        this.recarregarHistorico();
      },
      error: () => {
        this.idOrigemLead = anterior;
        this.alertService.show('Erro', 'Não foi possível trocar a origem do lead.', AlertType.error);
      },
    });
  }

  alterarCanalContato(idCanalContato: number | null): void {
    const anterior = this.card.idCanalContato;
    this.idCanalContato = idCanalContato;

    this.oportunidadeService.update(this.card.idOportunidade, { idCanalContato }).subscribe({
      next: response => {
        if (response.isError) {
          this.idCanalContato = anterior;
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível trocar o canal de contato.', AlertType.warning);
          return;
        }
        this.card.idCanalContato = idCanalContato;
        this.recarregarHistorico();
      },
      error: () => {
        this.idCanalContato = anterior;
        this.alertService.show('Erro', 'Não foi possível trocar o canal de contato.', AlertType.error);
      },
    });
  }

  origemNome(idOrigemLead: number | null): string {
    return this.origens.find(o => o.id === idOrigemLead)?.descricao ?? '';
  }

  canalNome(idCanalContato: number | null): string {
    if (idCanalContato == null) return '';
    return this.canais.find(c => c.id === idCanalContato)?.descricao ?? '';
  }

  
  definirValorEstimadoNativo(valor: number | null): void {
    this.valorEstimado = valor;
    this.valorEstimadoSujo = true;
    this.valorEstimadoAlterado$.next();
  }

  private persistirValorEstimado(): void {
    if (!this.valorEstimadoSujo) return;
    this.valorEstimadoSujo = false;

    const anterior = this.card.valorEstimado;
    const valor = this.valorEstimado ?? 0;

    this.oportunidadeService.update(this.card.idOportunidade, { valorEstimado: valor }).subscribe({
      next: response => {
        if (response.isError) {
          this.valorEstimado = anterior;
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível salvar o valor estimado.', AlertType.warning);
          return;
        }
        this.card.valorEstimado = valor;
        // Sem documento vinculado, o valor da oportunidade é o próprio estimado.
        if (this.card.idVendaValor == null) this.card.valor = valor;
        this.recarregarHistorico();
      },
      error: () => {
        this.valorEstimado = anterior;
        this.alertService.show('Erro', 'Não foi possível salvar o valor estimado.', AlertType.error);
      },
    });
  }

  get statusInfo(): StatusClienteInfo {
    return this.statusOpcoes.find(s => s.chave === this.statusCliente) ?? STATUS_CLIENTE_INDEFINIDO;
  }

  get prioridadeInfo(): PrioridadeInfo {
    return PRIORIDADE_POR_CHAVE[this.card.prioridade] ?? PRIORIDADE_POR_CHAVE.media;
  }

  /** Usuário responsável pela ação pendente. */
  get responsavelAcao(): Person | undefined {
    return this.data.pessoas.find(p => p.idUsuario === this.card.idUsuarioResponsavelAcao);
  }

  get criadoPorNome(): string {
    return this.data.pessoas.find(p => p.idUsuario === this.card.idUsuarioInclusao)?.name ?? '—';
  }

  /** O atual entra mesmo inativo ou fora do escopo: a oportunidade continua com ele até a troca. */
  private montarOpcoesVendedor(): void {
    const visiveis = this.data.idsVendedoresVisiveis ?? [];
    const opcoes = (this.data.vendedores ?? []).filter(v => v.idVendedor === this.card.idVendedorResponsavel
      || (v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo
        && (!visiveis.length || visiveis.includes(v.idVendedor))));

    this.opcoesVendedor = opcoes.map(v => ({ ...v, nome: nomeDoVendedor(v) }));
  }

  /** Ação pendente: o responsável dela; ação nova: quem está criando (o usuário logado). */
  private usuarioResponsavelSugerido(): number | null {
    return this.card.idUsuarioResponsavelAcao
      ?? this.authService.getUserInfos()?.idUsuario
      ?? null;
  }

  
  alterarStatus(status: StatusClienteChave): void {
    if (this.motivoStatusSemPermissao) return;
    const anterior = this.statusCliente;
    this.statusCliente = status;

    this.pessoaService.atualizarStatusCrm(this.card.idCliente, status).subscribe({
      next: response => {
        if (response.isError) {
          this.statusCliente = anterior;
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível alterar o status.', AlertType.warning);
          return;
        }

        const rotulo = (chave: StatusClienteChave) =>
          this.data.statusOpcoes.find(o => o.chave === chave)?.label ?? String(chave);
        const label = rotulo(status);

        // O status já foi gravado no cadastro do cliente: falha no histórico só avisa, não desfaz a troca.
        const avisarFalhaHistorico = () => this.alertService.show(
          'Aviso', 'Status alterado, mas não foi possível registrar no Histórico.', AlertType.warning);
        this.oportunidadeService
          .registrarStatusCliente(this.card.idOportunidade, {
            statusCliente: label,
            statusClienteAnterior: anterior ? rotulo(anterior) : null,
          })
          .subscribe({
            next: response => {
              if (response.isError) avisarFalhaHistorico();
              this.recarregarHistorico();
            },
            error: () => {
              avisarFalhaHistorico();
              this.recarregarHistorico();
            },
          });
      },
      error: () => {
        this.statusCliente = anterior;
        this.alertService.show('Erro', 'Não foi possível alterar o status.', AlertType.error);
      },
    });
  }

  isStatusAtual(status: StatusClienteChave): boolean {
    return this.statusCliente === status;
  }

  
  alterarPrioridade(prioridade: PrioridadeChave): void {
    const anterior = this.card.prioridade;
    this.card.prioridade = prioridade;

    this.oportunidadeService
      .update(this.card.idOportunidade, { idTipoPrioridade: ID_POR_PRIORIDADE[prioridade] })
      .subscribe({
        next: resposta => {
          if (resposta.isError) {
            this.card.prioridade = anterior;
            this.alertService.show(
              'Atenção', resposta.errorDescription || 'Não foi possível alterar a prioridade.',
              AlertType.warning);
            return;
          }
          this.recarregarHistorico();
        },
        error: () => {
          this.card.prioridade = anterior;
          this.alertService.show('Erro', 'Não foi possível alterar a prioridade.', AlertType.error);
        },
      });
  }

  isPrioridadeAtual(prioridade: PrioridadeChave): boolean {
    return this.card.prioridade === prioridade;
  }

  iniciarEdicaoTitulo(): void {
    this.tituloDraft = this.card.titulo;
    this.tituloEditando = true;
  }

  salvarTitulo(): void {
    const titulo = this.tituloDraft.trim();
    if (!titulo || titulo === this.card.titulo) {
      this.tituloEditando = false;
      return;
    }

    this.oportunidadeService.update(this.card.idOportunidade, { titulo }).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível salvar o assunto.', AlertType.warning);
          return;
        }
        this.card.titulo = titulo;
        this.tituloEditando = false;
        this.recarregarHistorico();
      },
      error: () => this.alertService.show('Erro', 'Não foi possível salvar o assunto.', AlertType.error),
    });
  }

  cancelarTitulo(): void {
    this.tituloEditando = false;
  }

  /**
   * Estrela antes do título. Escreve no card compartilhado com o board: a estrela do card já aparece trocada e,
   * ao fechar, o board recarrega (na visão Favoritas, o card desfavoritado sai).
   */
  alternarFavorito(): void {
    if (this.isFavoritando) return;

    const favoritar = !this.card.isFavorito;
    this.card.isFavorito = favoritar;
    this.isFavoritando = true;

    this.oportunidadeService.favoritar(this.card.idOportunidade, favoritar).subscribe({
      next: response => {
        this.isFavoritando = false;
        if (response.isError) {
          this.card.isFavorito = !favoritar;
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível atualizar os favoritos.', AlertType.warning);
          return;
        }

        this.alertService.show(
          'Sucesso',
          favoritar ? 'Oportunidade marcada como favorita.' : 'Oportunidade removida dos favoritos.',
          AlertType.success
        );
      },
      error: () => {
        this.isFavoritando = false;
        this.card.isFavorito = !favoritar;
        this.alertService.show(
          'Aviso', 'Não foi possível atualizar os favoritos agora. Tente novamente em alguns instantes.',
          AlertType.warning);
      },
    });
  }

  
  abrirPainel(painel: PainelFicha): void {
    this.painel = painel;
    this.moverAberto = false;

    if (painel === 'tags') this.tagsControl.setValue(this.card.tags.map(t => t.idTagCrm));
    if (painel === 'outros') this.carregarOutrosAssuntos();
  }

  fecharPainel(): void {
    this.painel = null;
    this.historicoSelecionado = null;
  }

  /** A timeline devolve o próprio objeto recebido em `itens`. */
  abrirDetalheHistorico(item: TimelineHistoricoItem): void {
    this.historicoSelecionado = item as HistoricoOportunidadeItem;
    this.abrirPainel('historico');
  }

  /** O diff grava o id do vendedor responsável (ocorrência Vendedor alterado): o detalhe mostra o nome. */
  private formatarAlteracao(alteracao: AuditChange): AuditChange {
    if (alteracao.campo !== 'idVendedorResponsavel') return alteracao;

    const nome = (valor?: string | null): string | null | undefined => {
      if (!valor) return valor;
      const id = Number(valor);
      if (!Number.isFinite(id)) return valor;
      return nomeDoVendedor(this.vendedoresPorId.get(id));
    };

    return { ...alteracao, de: nome(alteracao.de), para: nome(alteracao.para) };
  }

  /**
   * Abre o formulário no topo da linha do tempo: fecha o anterior sem salvar, troca para a aba, rola a área
   * principal ao topo e põe o cursor no primeiro campo. `registro` é a linha editada (ou o e-mail a reenviar).
   */
  abrirFormularioTimeline(tipo: FormularioTimeline, registro: HistoricoOportunidadeItem | null = null): void {
    this.fecharPainel();
    this.moverAberto = false;
    this.registroEditando = registro;

    switch (tipo) {
      case 'acao':
        this.acaoTexto = this.card.proximaAcao ?? '';
        this.acaoData = this.textoParaData(this.card.dataProximaAcao);
        this.acaoComData = !!this.card.dataProximaAcao || !this.card.proximaAcao;
        this.acaoResponsavel = this.usuarioResponsavelSugerido();
        // Editar a ação pendente esmaece o registro dela na linha do tempo, de onde quer que venha.
        this.registroEditando = registro ?? this.registroDaAcaoPendente();
        break;
      case 'email': {
        // Reenvio: os campos do e-mail gravado; registro antigo, sem dados, abre como um e-mail novo.
        const temDados = registro?.emailDestinatario != null || registro?.emailAssunto != null;
        this.emailPara = (temDados ? registro?.emailDestinatario : null) ?? this.card.contatoEmail ?? '';
        this.emailAssunto = (temDados ? registro?.emailAssunto : null) ?? this.card.titulo;
        this.emailMensagem = temDados ? registro?.detalhe ?? '' : '';
        break;
      }
      case 'anotacao':
        this.anotacaoTexto = registro?.descricao ?? '';
        break;
      case 'acaoConcluida':
        // O mesmo texto que a linha mostra (sem o prefixo "Ação concluída: " / "Próxima ação definida: ").
        this.acaoConcluidaTexto = registro?.tituloLinhaDoTempo ?? registro?.descricao ?? '';
        break;
    }

    this.formularioTimeline = tipo;
    this.aba = 'timeline';

    setTimeout(() => {
      this.areaPrincipal?.nativeElement.scrollTo({ top: 0 });
      // Em tela menor a ficha empilha as colunas e quem rola é a ficha inteira: sem levar o
      // formulário até a vista, ele abre lá embaixo e parece que o botão não fez nada.
      const campo = this.primeiroCampoTimeline?.nativeElement;
      campo?.closest('.ocd-tl-form')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      campo?.focus({ preventScroll: true });
    });
  }

  fecharFormularioTimeline(): void {
    this.formularioTimeline = null;
    this.registroEditando = null;
  }

  get tituloFormularioTimeline(): string {
    switch (this.formularioTimeline) {
      case 'acao':
        return this.temAcaoPendente ? 'Editar ação' : 'Nova ação';
      case 'email':
        return this.registroEditando ? 'Editar e-mail' : 'Enviar e-mail';
      case 'anotacao':
        return this.registroEditando ? 'Editar anotação' : 'Nova anotação';
      case 'acaoConcluida':
        return this.registroEditando?.idTipoOcorrenciaOportunidade === TipoOcorrenciaOportunidadeEnum.AcaoPendente
          ? 'Editar ação'
          : 'Editar ação concluída';
      default:
        return '';
    }
  }

  get iconeFormularioTimeline(): string {
    switch (this.formularioTimeline) {
      case 'acao':
        return 'push_pin';
      case 'email':
        return 'mail';
      case 'anotacao':
        return 'sticky_note_2';
      default:
        return this.registroEditando?.icone || 'task_alt';
    }
  }

  /** Sem e-mail no contato e sem e-mail gravado para reenviar, não há destinatário a sugerir. */
  get semEnderecoEmail(): boolean {
    return !this.card.contatoEmail && !this.registroEditando;
  }

  /** Enviar e reenviar geram um registro novo na linha do tempo; o original fica. */
  enviarEmail(): void {
    const destinatario = this.emailPara.trim();
    if (!destinatario || this.salvandoFormulario) return;

    const assunto = this.emailAssunto.trim() || `Oportunidade ${this.card.id}`;
    this.salvandoFormulario = true;

    this.oportunidadeService
      .enviarEmail(this.card.idOportunidade, { destinatario, assunto, mensagem: this.emailMensagem })
      .subscribe({
        next: response => {
          this.salvandoFormulario = false;

          if (response.isError) {
            this.alertService.show(
              'Aviso', response.errorDescription || 'Não foi possível enviar a mensagem.', AlertType.warning);
            return;
          }

          this.fecharFormularioTimeline();
          this.recarregarHistorico();
          this.alertService.show('Sucesso', `Mensagem enviada para ${destinatario}.`, AlertType.success);
        },
        error: () => {
          this.salvandoFormulario = false;
          this.alertService.show('Aviso',
            'Não foi possível enviar a mensagem agora. Tente novamente em alguns instantes.', AlertType.warning);
        },
      });
  }

  /**
   * Salvar da anotação (nova ou editada) e da ação concluída editada. Anotação nova vai sem data, para o servidor
   * gravar a hora do registro.
   */
  salvarRegistroTimeline(): void {
    const anotacao = this.formularioTimeline === 'anotacao';
    const descricao = (anotacao ? this.anotacaoTexto : this.acaoConcluidaTexto).trim();
    const registro = this.registroEditando;
    if (!descricao || this.salvandoFormulario || (!anotacao && !registro)) return;

    let requisicao: Observable<ReturnStruct>;
    if (registro) {
      const request = new OportunidadeOcorrenciaEditarRequest();
      request.descricao = descricao;
      requisicao = this.oportunidadeService.atualizarOcorrencia(
        this.card.idOportunidade, registro.idOportunidadeOcorrencia, request);
    } else {
      const request = new CriarOportunidadeOcorrenciaRequest();
      request.descricao = descricao;
      requisicao = this.oportunidadeService.criarOcorrencia(this.card.idOportunidade, request);
    }

    const sucesso = !anotacao ? 'Registro atualizado.' : registro ? 'Anotação atualizada.' : 'Anotação registrada.';
    const falha = !anotacao
      ? 'Não foi possível atualizar o registro'
      : registro ? 'Não foi possível atualizar a anotação' : 'Não foi possível registrar a anotação';

    this.salvandoFormulario = true;
    requisicao.subscribe({
      next: response => {
        this.salvandoFormulario = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription || `${falha}.`, AlertType.warning);
          return;
        }
        this.fecharFormularioTimeline();
        this.recarregarHistorico();
        this.alertService.show('Sucesso', sucesso, AlertType.success);
      },
      error: () => {
        this.salvandoFormulario = false;
        this.alertService.show('Aviso', `${falha} agora. Tente novamente em alguns instantes.`, AlertType.warning);
      },
    });
  }

  /** Ícone editar da linha: abre no topo o formulário do tipo, preenchido. */
  editarRegistroTimeline(registro: HistoricoOportunidadeItem): void {
    this.registroConfirmandoExclusao = null;

    switch (registro.idTipoOcorrenciaOportunidade) {
      case TipoOcorrenciaOportunidadeEnum.AcaoPendente:
        this.abrirFormularioTimeline(registro === this.registroDaAcaoPendente() ? 'acao' : 'acaoConcluida', registro);
        break;
      case TipoOcorrenciaOportunidadeEnum.EnvioEmail:
        this.abrirFormularioTimeline('email', registro);
        break;
      case TipoOcorrenciaOportunidadeEnum.Anotacao:
        this.abrirFormularioTimeline('anotacao', registro);
        break;
      case TipoOcorrenciaOportunidadeEnum.AcaoConcluida:
        this.abrirFormularioTimeline('acaoConcluida', registro);
        break;
    }
  }

  /** O excluir da linha só abre a confirmação na própria linha; o "Excluir" dela é que exclui. */
  pedirExclusaoRegistroTimeline(registro: HistoricoOportunidadeItem): void {
    this.registroConfirmandoExclusao = registro;
  }

  cancelarExclusaoRegistroTimeline(): void {
    this.registroConfirmandoExclusao = null;
  }

  /** Já confirmado na linha. O registro de ação pendente leva junto a ação ainda não concluída (API). */
  excluirRegistroTimeline(registro: HistoricoOportunidadeItem): void {
    this.registroConfirmandoExclusao = null;
    if (this.itemExcluindo) return;

    // Mesmo critério da API: sem vínculo gravado, a pendente atual; com vínculo, a ação se não foi concluída.
    const levaAcaoPendente = registro.idTipoOcorrenciaOportunidade === TipoOcorrenciaOportunidadeEnum.AcaoPendente
      && this.temAcaoPendente
      && (registro.idOportunidadeAcao == null || !this.acaoFoiConcluida(registro.idOportunidadeAcao));

    this.itemExcluindo = registro;

    this.oportunidadeService.excluirOcorrencia(this.card.idOportunidade, registro.idOportunidadeOcorrencia).subscribe({
      next: response => {
        this.itemExcluindo = null;
        if (response.isError) {
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível excluir o registro.', AlertType.warning);
          return;
        }

        if (levaAcaoPendente) {
          this.card.proximaAcao = null;
          this.card.dataProximaAcao = null;
          this.card.idUsuarioResponsavelAcao = null;
          if (this.formularioTimeline === 'acao') this.fecharFormularioTimeline();
        }

        this.recarregarHistorico();
        this.alertService.show('Sucesso', 'Registro excluído.', AlertType.success);
      },
      error: () => {
        this.itemExcluindo = null;
        this.alertService.show('Aviso',
          'Não foi possível excluir o registro agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private acaoFoiConcluida(idOportunidadeAcao: number): boolean {
    return this.historico.some(item =>
      item.idTipoOcorrenciaOportunidade === TipoOcorrenciaOportunidadeEnum.AcaoConcluida
      && item.idOportunidadeAcao === idOportunidadeAcao);
  }

  /** Registro da ação pendente: o 21 mais recente, enquanto o card tem ação e ela não foi concluída. */
  private registroDaAcaoPendente(): HistoricoOportunidadeItem | null {
    if (!this.temAcaoPendente) return null;

    let ultimo: HistoricoOportunidadeItem | null = null;
    for (const item of this.historicoTimeline) {
      if (item.idTipoOcorrenciaOportunidade !== TipoOcorrenciaOportunidadeEnum.AcaoPendente) continue;
      if (!ultimo || new Date(item.quando).getTime() > new Date(ultimo.quando).getTime()) ultimo = item;
    }

    if (ultimo?.idOportunidadeAcao != null && this.acaoFoiConcluida(ultimo.idOportunidadeAcao)) return null;
    return ultimo;
  }

  /** Editar e excluir por linha (CA-22); orçamento fica para o Incremento 2. */
  private definirAcoesDaLinhaDoTempo(): void {
    for (const item of this.historicoTimeline) {
      switch (item.idTipoOcorrenciaOportunidade) {
        case TipoOcorrenciaOportunidadeEnum.Orcamento:
        case TipoOcorrenciaOportunidadeEnum.OrcamentoFormalizado:
          item.podeEditar = false;
          item.motivoNaoEditavel = 'Registro de orçamento não pode ser editado aqui.';
          item.podeExcluir = false;
          item.motivoNaoExcluivel = 'Registro de orçamento não pode ser excluído aqui.';
          break;
        case TipoOcorrenciaOportunidadeEnum.Encerramento:
        case TipoOcorrenciaOportunidadeEnum.Reabertura:
          item.podeEditar = false;
          item.motivoNaoEditavel = 'Registro de encerramento ou reabertura não pode ser editado.';
          item.podeExcluir = false;
          item.motivoNaoExcluivel = 'Registro de encerramento ou reabertura não pode ser excluído.';
          break;
        case TipoOcorrenciaOportunidadeEnum.AcaoPendente:
          // O registro da ação ainda pendente abre o formulário de ação; o de uma ação já concluída edita só o texto.
          item.podeEditar = this.podeAlterar;
          item.motivoNaoEditavel = this.podeAlterar ? undefined : 'Sem permissão para alterar registros da linha do tempo.';
          item.podeExcluir = this.podeExcluirRegistro;
          item.motivoNaoExcluivel = this.podeExcluirRegistro ? undefined : 'Sem permissão para excluir registros da linha do tempo.';
          break;
        default:
          item.podeEditar = this.podeAlterar;
          item.motivoNaoEditavel = this.podeAlterar ? undefined : 'Sem permissão para alterar registros da linha do tempo.';
          item.podeExcluir = this.podeExcluirRegistro;
          item.motivoNaoExcluivel = this.podeExcluirRegistro ? undefined : 'Sem permissão para excluir registros da linha do tempo.';
      }
    }
  }

  salvarTags(): void {
    // Preserva a ordem do catálogo, para o card não embaralhar os chips a cada edição.
    const selecionadas = new Set(this.tagsControl.value);
    const idsTagCrm = this.data.tagsDisponiveis
      .filter(t => selecionadas.has(t.idTagCrm))
      .map(t => t.idTagCrm);

    this.oportunidadeService.salvarTags(this.card.idOportunidade, { idsTagCrm }).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível salvar as tags.', AlertType.warning);
          return;
        }
        this.card.tags = response.tags ?? [];
        this.recarregarHistorico();
        this.painel = null;
      },
      error: () => this.alertService.show('Erro', 'Não foi possível salvar as tags.', AlertType.error),
    });
  }

  
  outrosAssuntos: OutroAssuntoFicha[] = [];
  carregandoOutrosAssuntos = false;

  private carregarOutrosAssuntos(): void {
    this.carregandoOutrosAssuntos = true;
    this.outrosAssuntos = [];

    this.oportunidadeService
      .getLista({ idPessoaCliente: this.card.idCliente }, 0, 50)
      .subscribe({
        next: resposta => {
          this.carregandoOutrosAssuntos = false;
          if (resposta.isError) return;

          this.outrosAssuntos = (resposta.itens ?? [])
            .filter(item => item.idOportunidade !== this.card.idOportunidade)
            .map(item => ({
              idOportunidade: item.idOportunidade,
              codigo: item.codigo,
              titulo: item.titulo,
              etapa: this.stages.find(s => s.key === item.chaveEtapa)?.name ?? '—',
              arquivado: item.isArquivado,
            }));
        },
        error: () => {
          this.carregandoOutrosAssuntos = false;
          this.alertService.show(
            'Erro', 'Não foi possível carregar os outros assuntos.', AlertType.error);
        },
      });
  }

  
  abrirOutroAssunto(idOportunidade: number): void {
    this.fechar(this.card.stage !== this.etapaOriginal ? 'movido' : 'atualizado', idOportunidade);
  }

  excluirAssunto(): void {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Excluir assunto',
          description: `O assunto "${this.card.titulo}" será removido do board. Confirma a exclusão?`,
          buttonConfirm: 'Excluir',
          buttonClose: 'Cancelar',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (!result?.confirm) return;

        this.oportunidadeService.delete(this.card.idOportunidade).subscribe({
          next: response => {
            if (response.isError) {
              this.alertService.show(
                'Atenção', response.errorDescription || 'Não foi possível excluir o assunto.', AlertType.warning);
              return;
            }
            this.fechar('excluido');
          },
          error: () => this.alertService.show('Erro', 'Não foi possível excluir o assunto.', AlertType.error),
        });
      });
  }

  get temAcaoPendente(): boolean {
    return !!this.card.proximaAcao;
  }

  get acaoVencida(): boolean {
    const data = this.textoParaData(this.card.dataProximaAcao);
    if (!data) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return data.getTime() < hoje.getTime();
  }

  get acaoBadge(): { label: string; vencida: boolean } {
    if (!this.card.dataProximaAcao) return { label: 'Sem data', vencida: false };
    return { label: this.card.dataProximaAcao, vencida: this.acaoVencida };
  }

  /** "Salvar" do formulário de ação: cria a ação pendente ou edita a que existe. */
  salvarAcao(): void {
    const texto = this.acaoTexto.trim();
    if (!texto || this.salvandoFormulario) return;

    const dataPrevista = this.acaoComData && this.acaoData ? this.paraIso(this.acaoData) : null;
    const idUsuarioResponsavel = this.acaoResponsavel ?? this.authService.getUserInfos()?.idUsuario ?? 0;
    this.salvandoFormulario = true;

    this.oportunidadeService
      .salvarProximaAcao(this.card.idOportunidade, { descricao: texto, dataPrevista, idUsuarioResponsavel })
      .subscribe({
        next: response => {
          this.salvandoFormulario = false;
          if (response.isError) {
            this.alertService.show(
              'Aviso', response.errorDescription || 'Não foi possível salvar a ação.', AlertType.warning);
            return;
          }

          this.card.proximaAcao = texto;
          this.card.dataProximaAcao =
            this.acaoComData && this.acaoData ? this.formatarData(this.acaoData) : null;
          this.card.idUsuarioResponsavelAcao = idUsuarioResponsavel;

          this.fecharFormularioTimeline();
          this.recarregarHistorico();
          this.alertService.show('Sucesso', 'Próxima ação definida.', AlertType.success);
        },
        error: () => {
          this.salvandoFormulario = false;
          this.alertService.show('Aviso',
            'Não foi possível salvar a ação agora. Tente novamente em alguns instantes.', AlertType.warning);
        },
      });
  }

  concluirAcao(): void {
    if (!this.card.proximaAcao) return;
    const descricao = this.card.proximaAcao;

    this.oportunidadeService.concluirProximaAcao(this.card.idOportunidade).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível concluir a ação.', AlertType.warning);
          return;
        }
        this.card.proximaAcao = null;
        this.card.dataProximaAcao = null;
        this.card.idUsuarioResponsavelAcao = null;
        if (this.formularioTimeline === 'acao') this.fecharFormularioTimeline();
        this.recarregarHistorico();
        this.alertService.show('Sucesso', 'Ação concluída.', AlertType.success);
      },
      error: () => this.alertService.show('Erro', 'Não foi possível concluir a ação.', AlertType.error),
    });
  }

  /**
   * Troca de vendedor (RN-12): antes de gravar, pergunta se os orçamentos em aberto vinculados vão junto. Sem
   * orçamento em aberto, grava direto.
   */
  onVendedorSelecionado(selecionado: VendedorLookupStruct | number | null): void {
    const idVendedor = typeof selecionado === 'object' && selecionado ? selecionado.idVendedor : selecionado;
    if (idVendedor == null) return;

    const vendedor = this.vendedoresPorId.get(idVendedor);
    if (!vendedor || idVendedor === this.card.idVendedorResponsavel) {
      this.trocaVendedor = null;
      return;
    }

    this.trocaVendedor = null;
    this.consultandoOrcamentosEmAberto = true;

    this.oportunidadeService.getOrcamentosEmAberto(this.card.idOportunidade, idVendedor).subscribe({
      next: response => {
        this.consultandoOrcamentosEmAberto = false;
        if (response.isError) {
          this.vendedorControl.setValue(this.card.idVendedorResponsavel, { emitEvent: false });
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível trocar o vendedor responsável.', AlertType.warning);
          return;
        }

        if (!response.quantidade) {
          this.atribuirVendedor(idVendedor);
          return;
        }

        this.trocaVendedor = { idVendedor, nome: nomeDoVendedor(vendedor), quantidade: response.quantidade };
        // Sim é a resposta padrão: recebe o foco assim que a pergunta aparece.
        setTimeout(() => this.botaoSimTrocaVendedor?.nativeElement.focus());
      },
      error: () => {
        this.consultandoOrcamentosEmAberto = false;
        this.vendedorControl.setValue(this.card.idVendedorResponsavel, { emitEvent: false });
        this.alertService.show('Aviso',
          'Não foi possível trocar o vendedor responsável agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  get textoTrocaVendedor(): string {
    if (!this.trocaVendedor) return '';
    const { quantidade, nome } = this.trocaVendedor;
    return quantidade === 1
      ? `Levar o orçamento em aberto para ${nome}?`
      : `Levar os ${quantidade} orçamentos em aberto para ${nome}?`;
  }

  /** Sim e Não gravam a troca; a resposta só decide se os orçamentos em aberto vão junto. */
  responderTrocaVendedor(levarOrcamentosEmAberto: boolean): void {
    if (!this.trocaVendedor || this.salvandoVendedor) return;
    this.respostaTrocaVendedor = levarOrcamentosEmAberto;
    this.atribuirVendedor(this.trocaVendedor.idVendedor, levarOrcamentosEmAberto);
  }

  /** Cancelar: volta o vendedor anterior, sem gravar. */
  cancelarTrocaVendedor(): void {
    this.trocaVendedor = null;
    this.vendedorControl.setValue(this.card.idVendedorResponsavel, { emitEvent: false });
  }

  /** `levarOrcamentosEmAberto` só vai quando a pergunta foi feita (havia orçamento em aberto). */
  private atribuirVendedor(idVendedor: number, levarOrcamentosEmAberto?: boolean): void {
    const vendedor = this.vendedoresPorId.get(idVendedor);
    if (!vendedor) return;

    this.salvandoVendedor = true;

    this.oportunidadeService
      .update(this.card.idOportunidade, { idVendedorResponsavel: idVendedor, levarOrcamentosEmAberto })
      .subscribe({
        next: response => {
          this.salvandoVendedor = false;
          if (response.isError) {
            this.alertService.show(
              'Aviso', response.errorDescription || 'Não foi possível trocar o vendedor responsável.', AlertType.warning);
            this.cancelarTrocaVendedor();
            return;
          }
          this.trocaVendedor = null;
          this.card.idVendedorResponsavel = idVendedor;
          this.card.resp = iniciaisDoNome(vendedor.nome);
          this.card.respName = nomeDoVendedor(vendedor);
          this.card.respColor = corDoNome(vendedor.nome);
          this.vendedorControl.setValue(idVendedor, { emitEvent: false });
          // O antigo, se inativo, sai das opções: novas atribuições só aceitam ativos.
          this.montarOpcoesVendedor();
          this.recarregarHistorico();
          // A troca ficou gravada; o aviso diz o que não aconteceu com os orçamentos.
          if (response.aviso) this.alertService.show('Aviso', response.aviso, AlertType.warning);
        },
        error: () => {
          this.salvandoVendedor = false;
          this.alertService.show('Aviso',
            'Não foi possível trocar o vendedor responsável agora. Tente novamente em alguns instantes.', AlertType.warning);
          this.cancelarTrocaVendedor();
        },
      });
  }

  
  private paraIso(data: Date): string {
    const mes = `${data.getMonth() + 1}`.padStart(2, '0');
    const dia = `${data.getDate()}`.padStart(2, '0');
    return `${data.getFullYear()}-${mes}-${dia}`;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  get podeMover(): boolean {
    return !this.etapaAtual?.isFinal;
  }

  private destinosPermitidos(): OportunidadeStage[] {
    if (!this.podeMover) return [];
    const chaves = this.data.transicoes[this.card.stage] ?? [];
    return this.stages.filter(s => chaves.includes(s.key) && !s.isFinal);
  }

  get destinosAvancar(): OportunidadeStage[] {
    return this.destinosPermitidos().filter(
      s => this.stages.indexOf(s) > this.indiceEtapaAtual
    );
  }

  get destinosRetornar(): OportunidadeStage[] {
    return this.destinosPermitidos().filter(
      s => this.stages.indexOf(s) < this.indiceEtapaAtual
    );
  }

  
  get etapasFinais(): OportunidadeStage[] {
    return this.stages.filter(s => s.isFinal);
  }

  toggleMover(): void {
    this.moverAberto = !this.moverAberto;
  }

  mover(destino: OportunidadeStage): void {
    this.moverAberto = false;

    this.envolvidosEtapaService
      .getEnvolvidos(destino.idEtapaKanban)
      .pipe(
        switchMap(idsUsuarioNotificar => this.oportunidadeService.mover(this.card.idOportunidade, {
          idEtapaKanbanDestino: destino.idEtapaKanban,
          idsUsuarioNotificar,
        }))
      )
      .subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show(
              'Atenção', response.errorDescription || 'Não foi possível mover o card.', AlertType.warning);
            return;
          }
          this.recarregarHistorico();
          this.aplicarMovimentacao(destino);
          this.alertService.show('Sucesso', `Card movido para ${destino.name}.`, AlertType.success);
        },
        error: () => this.alertService.show('Erro', 'Não foi possível mover o card.', AlertType.error),
      });
  }

  
  encerrar(final: OportunidadeStage | undefined): void {
    if (!final) {
      this.alertService.show(
        'Atenção',
        'Nenhuma etapa final configurada no fluxo. Configure em Configurações › Vendas › Configuração do Kanban de Oportunidades.',
        AlertType.warning
      );
      return;
    }

    this.moverAberto = false;

    this.dialog
      .open(EncerrarOportunidadeDialogComponent, {
        data: {
          titulo: this.card.titulo,
          cliente: this.card.cliente,
          etapaNome: final.name,
          resultados: this.data.resultados,
          motivos: this.data.motivos,
        },
        autoFocus: false,
        maxWidth: '92vw',
      })
      .afterClosed()
      .subscribe((resultado?: EncerrarOportunidadeResult | null) => {
        if (!resultado) return;

        if (resultado.idCampoDefinicaoOpcaoResultado == null || resultado.idCampoDefinicaoOpcaoMotivo == null) {
          this.alertService.show(
            'Atenção', 'Resultado ou motivo não reconhecido. Recarregue a tela.', AlertType.warning);
          return;
        }

        this.oportunidadeService
          .encerrar(this.card.idOportunidade, {
            idEtapaKanbanDestino: final.idEtapaKanban,
            idCampoDefinicaoOpcaoResultado: resultado.idCampoDefinicaoOpcaoResultado,
            idCampoDefinicaoOpcaoMotivo: resultado.idCampoDefinicaoOpcaoMotivo,
            observacoesFinais: resultado.observacoes,
          })
          .subscribe({
            next: response => {
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  response.errorDescription || 'Não foi possível encerrar a oportunidade.',
                  AlertType.warning
                );
                return;
              }

              this.card.resultado = resultado.resultado;
              this.card.motivo = resultado.motivo;
              this.card.idCampoDefinicaoOpcaoResultado = resultado.idCampoDefinicaoOpcaoResultado;
              this.card.idCampoDefinicaoOpcaoMotivo = resultado.idCampoDefinicaoOpcaoMotivo;
              this.card.observacoesFinais = resultado.observacoes;
              this.card.proximaAcao = null;
              this.card.dataProximaAcao = null;

              this.recarregarHistorico();
              this.aplicarMovimentacao(final);
              this.alertService.show(
                'Oportunidade encerrada',
                `${this.card.titulo} — ${resultado.resultado}.`,
                AlertType.success
              );
            },
            error: () =>
              this.alertService.show('Erro', 'Não foi possível encerrar a oportunidade.', AlertType.error),
          });
      });
  }

  reabrir(): void {
    const atual = this.etapaAtual;
    const destino = this.stages.find(s => s.idEtapaKanban === atual?.idEtapaKanbanRetornoReabertura);

    if (!destino) {
      this.alertService.show(
        'Atenção',
        `A etapa "${atual?.name}" não tem etapa de retorno configurada. Defina-a em Configurações › Vendas › Configuração do Kanban de Oportunidades.`,
        AlertType.warning
      );
      return;
    }

    this.oportunidadeService.reabrir(this.card.idOportunidade).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível reabrir a oportunidade.', AlertType.warning);
          return;
        }
        this.card.resultado = undefined;
        this.card.motivo = undefined;
        this.card.idCampoDefinicaoOpcaoResultado = null;
        this.card.idCampoDefinicaoOpcaoMotivo = null;
        this.recarregarHistorico();
        this.aplicarMovimentacao(destino);
        this.alertService.show('Oportunidade reaberta', `O card voltou para ${destino.name}.`, AlertType.success);
      },
      error: () => this.alertService.show('Erro', 'Não foi possível reabrir a oportunidade.', AlertType.error),
    });
  }

  
  private aplicarMovimentacao(destino: OportunidadeStage): void {
    this.card.stage = destino.key;
    this.card.idEtapaKanban = destino.idEtapaKanban;
    this.card.updatedAt = Date.now();
    this.card.renderKey = `${this.card.id}-${destino.key}-${this.card.updatedAt}`;
    this.houveMudanca = true;
    this.aba = destino.isFinal ? 'timeline' : 'etapa';
    this.recalcularGruposEtapa();
    // De volta à Prospecção: o Contato editável precisa da lista do cliente.
    if (this.prospeccaoAtual && !this.contatos.length && !this.carregandoContatos) {
      this.carregarContatos(this.card.idCliente);
    }
  }

  
  readonly tiposArquivoAceitos = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.txt,.csv';

  
  arquivosSoltos(lista: FileList): void {
    if (!lista.length) return;

    if (lista.length > 1) {
      this.alertService.show(
        'Atenção', 'Envie um arquivo por vez. Só o primeiro foi enviado.', AlertType.warning);
    }

    this.enviarArquivo(lista[0]);
  }

  
  private enviarArquivo(arquivo: File): void {
    if (arquivo.size > 10 * 1024 * 1024) {
      this.alertService.show('Atenção', 'O arquivo excede o limite de 10 MB.', AlertType.warning);
      return;
    }

    this.enviandoAnexo = true;
    const leitor = new FileReader();

    leitor.onerror = () => {
      this.enviandoAnexo = false;
      this.alertService.show('Erro', 'Não foi possível ler o arquivo.', AlertType.error);
    };

    leitor.onload = () => {
      const base64 = String(leitor.result ?? '').split(',')[1] ?? '';

      this.oportunidadeService
        .uploadAnexo(this.card.idOportunidade, {
          nomeArquivo: arquivo.name,
          base64,
          tamanhoBytes: arquivo.size,
        })
        .subscribe({
          next: response => {
            this.enviandoAnexo = false;
            if (response.isError || !response.anexo) {
              this.alertService.show(
                'Atenção', response.errorDescription || 'Não foi possível enviar o anexo.', AlertType.warning);
              return;
            }
            this.anexos = [response.anexo, ...this.anexos];
            this.recarregarHistorico();
            this.alertService.show('Sucesso', 'Anexo enviado.', AlertType.success);
          },
          error: () => {
            this.enviandoAnexo = false;
            this.alertService.show('Erro', 'Não foi possível enviar o anexo.', AlertType.error);
          },
        });
    };

    leitor.readAsDataURL(arquivo);
  }

  
  baixarAnexo(anexo: OportunidadeAnexo): void {
    this.oportunidadeService.getUrlAnexo(this.card.idOportunidade, anexo.idOportunidadeAnexo).subscribe({
      next: response => {
        if (response.isError || !response.urlArquivo) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível abrir o anexo.', AlertType.warning);
          return;
        }
        window.open(response.urlArquivo, '_blank');
      },
      error: () => this.alertService.show('Erro', 'Não foi possível abrir o anexo.', AlertType.error),
    });
  }

  excluirAnexo(anexo: OportunidadeAnexo): void {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Excluir anexo',
          description: `O arquivo "${anexo.nomeArquivo}" será removido. Confirma a exclusão?`,
          buttonConfirm: 'Excluir',
          buttonClose: 'Cancelar',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (!result?.confirm) return;

        this.oportunidadeService
          .deleteAnexo(this.card.idOportunidade, anexo.idOportunidadeAnexo)
          .subscribe({
            next: response => {
              if (response.isError) {
                this.alertService.show(
                  'Atenção', response.errorDescription || 'Não foi possível excluir o anexo.', AlertType.warning);
                return;
              }
              this.anexos = this.anexos.filter(a => a.idOportunidadeAnexo !== anexo.idOportunidadeAnexo);
              this.recarregarHistorico();
            },
            error: () => this.alertService.show('Erro', 'Não foi possível excluir o anexo.', AlertType.error),
          });
      });
  }

  tamanhoLegivel(bytes: number | null): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  enviarComentario(): void {
    const texto = this.comentarioTexto.trim();
    if (!texto) return;

    this.oportunidadeService.adicionarComentario(this.card.idOportunidade, { texto }).subscribe({
      next: response => {
        if (response.isError || !response.comentario) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível comentar.', AlertType.warning);
          return;
        }
        this.comentarios = [response.comentario, ...this.comentarios];
        this.comentarioTexto = '';
        this.houveMudanca = true;
      },
      error: () => this.alertService.show('Erro', 'Não foi possível comentar.', AlertType.error),
    });
  }

  iniciarEdicaoComentario(comentario: OportunidadeComentario): void {
    this.comentarioEmEdicao = comentario.idOportunidadeComentario;
    this.comentarioDraft = comentario.texto;
  }

  cancelarEdicaoComentario(): void {
    this.comentarioEmEdicao = null;
    this.comentarioDraft = '';
  }

  salvarEdicaoComentario(comentario: OportunidadeComentario): void {
    const texto = this.comentarioDraft.trim();
    if (!texto) return;

    this.oportunidadeService
      .atualizarComentario(this.card.idOportunidade, comentario.idOportunidadeComentario, { texto })
      .subscribe({
        next: response => {
          if (response.isError || !response.comentario) {
            this.alertService.show(
              'Atenção', response.errorDescription || 'Não foi possível editar o comentário.', AlertType.warning);
            return;
          }
          const atualizado = response.comentario;
          this.comentarios = this.comentarios.map(c =>
            c.idOportunidadeComentario === atualizado.idOportunidadeComentario ? atualizado : c);
          this.cancelarEdicaoComentario();
          this.houveMudanca = true;
        },
        error: () => this.alertService.show('Erro', 'Não foi possível editar o comentário.', AlertType.error),
      });
  }

  excluirComentario(comentario: OportunidadeComentario): void {
    this.oportunidadeService
      .deleteComentario(this.card.idOportunidade, comentario.idOportunidadeComentario)
      .subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show(
              'Atenção', response.errorDescription || 'Não foi possível excluir o comentário.', AlertType.warning);
            return;
          }
          this.comentarios = this.comentarios.filter(
            c => c.idOportunidadeComentario !== comentario.idOportunidadeComentario);
          this.houveMudanca = true;
        },
        error: () => this.alertService.show('Erro', 'Não foi possível excluir o comentário.', AlertType.error),
      });
  }

  autorDoComentario(idUsuario: number): Person | undefined {
    return this.data.pessoas.find(p => p.idUsuario === idUsuario);
  }

  dataCurta(iso: string): string {
    const data = dataDaApi(iso);
    return Number.isNaN(data.getTime()) ? '' : this.formatarData(data);
  }

  /** Depois de gravar: a ficha avisa o board e recarrega o histórico. */
  private recarregarHistorico(): void {
    this.houveMudanca = true;
    this.carregarHistorico();
  }

  private carregarHistorico(): void {
    this.oportunidadeService.getHistorico(this.card.idOportunidade).subscribe({
      next: resposta => {
        this.carregandoHistorico = false;
        if (resposta.isError) {
          this.alertService.show(
            'Aviso', resposta.errorDescription || 'Não foi possível carregar o histórico.', AlertType.warning);
          return;
        }
        this.historico = (resposta.listaOcorrencia ?? []).map(ocorrencia => this.mapearOcorrencia(ocorrencia));
        this.historicoTimeline = this.historico
          .filter(item => TIPOS_LINHA_DO_TEMPO.includes(item.idTipoOcorrenciaOportunidade))
          .sort((a, b) => new Date(b.quando).getTime() - new Date(a.quando).getTime());
        // Lateral: eventos do sistema do CRM junto com as ocorrências dos documentos de Vendas (AU-02), por data desc.
        const historicoVendas = (resposta.ocorrenciasVendas ?? []).map(ocorrencia => this.mapearOcorrenciaVendas(ocorrencia));
        this.historicoSistema = [
          ...this.historico.filter(item => !TIPOS_LINHA_DO_TEMPO.includes(item.idTipoOcorrenciaOportunidade)),
          ...historicoVendas,
        ].sort((a, b) => new Date(b.quando).getTime() - new Date(a.quando).getTime());
        this.definirAcoesDaLinhaDoTempo();
        this.montarLinhasDoTempo();

        // Recarga com o formulário aberto (ex.: campo da etapa salvo): a linha em edição continua esmaecida.
        const emEdicao = this.registroEditando;
        if (emEdicao) {
          this.registroEditando = this.historicoTimeline.find(
            item => item.idOportunidadeOcorrencia === emEdicao.idOportunidadeOcorrencia) ?? emEdicao;
        }
      },
      error: () => {
        this.carregandoHistorico = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar o histórico agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  /** Struct da API para o item do app-timeline-historico, no molde do montarHistorico da compra. */
  private mapearOcorrencia(ocorrencia: OportunidadeOcorrenciaStruct): HistoricoOportunidadeItem {
    let alteracoes: AuditChange[] | undefined;
    let idOportunidadeAcao: number | null = null;
    let emailDestinatario: string | null = null;
    let emailAssunto: string | null = null;
    if (ocorrencia.dados) {
      try {
        const parsed = JSON.parse(ocorrencia.dados);
        if (Array.isArray(parsed?.alteracoes) && parsed.alteracoes.length) {
          alteracoes = (parsed.alteracoes as AuditChange[]).map(alteracao => this.formatarAlteracao(alteracao));
        }
        // Ação (21, 22): a ação vinculada; e-mail (23): o que foi enviado, para o reenvio.
        idOportunidadeAcao = typeof parsed?.idOportunidadeAcao === 'number' ? parsed.idOportunidadeAcao : null;
        emailDestinatario = typeof parsed?.destinatario === 'string' ? parsed.destinatario : null;
        emailAssunto = typeof parsed?.assunto === 'string' ? parsed.assunto : null;
      } catch { /* dados malformado: fica só a descrição */ }
    }

    return {
      idOportunidadeOcorrencia: ocorrencia.idOportunidadeOcorrencia,
      idTipoOcorrenciaOportunidade: ocorrencia.idTipoOcorrenciaOportunidade,
      idUsuario: ocorrencia.idUsuario,
      icone: ocorrencia.nomeIconeTipoOcorrencia,
      titulo: ocorrencia.idTipoOcorrenciaOportunidade === TipoOcorrenciaOportunidadeEnum.VendedorAlterado
        ? 'Vendedor alterado'
        : ocorrencia.nomeTipoOcorrencia,
      descricao: ocorrencia.descricao,
      detalhe: ocorrencia.detalhes,
      justificativa: ocorrencia.justificativa,
      // "Detalhes" só aparece quando o painel de detalhe tem o diff para mostrar.
      dados: alteracoes ? ocorrencia.dados : null,
      quando: ocorrencia.dataOcorrencia,
      // Sem nome gravado (ocorrência migrada), o autor é o usuário do lookup ou a própria origem.
      quemNome: ocorrencia.nomeUsuario
        || this.autorDoComentario(ocorrencia.idUsuario ?? -1)?.name
        || ocorrencia.nomeOrigemOcorrencia,
      nomeOrigem: ocorrencia.nomeOrigemOcorrencia,
      iconeOrigem: ocorrencia.nomeIconeOrigemOcorrencia,
      exibirOrigem: ocorrencia.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
      nomeStatusAnterior: ocorrencia.nomeEtapaKanbanAnterior,
      nomeStatusAtual: ocorrencia.nomeEtapaKanbanAtual,
      alteracoes,
      idOportunidadeAcao,
      emailDestinatario,
      emailAssunto,
      ...this.textoLinhaDoTempo(ocorrencia, emailAssunto),
    };
  }

  /**
   * Ocorrência de um documento de Vendas no Histórico da lateral: só leitura (AU-04), com o documento, o autor e a
   * origem do catálogo, como as do CRM. Sem usuário, o autor é a própria origem.
   */
  private mapearOcorrenciaVendas(ocorrencia: VendaOcorrenciaOportunidadeStruct): TimelineHistoricoItem {
    const nomeDocumento = this.nomeDocumentoVendas(ocorrencia.idVendaTipoDocumento, ocorrencia.numero);
    const descricao = ocorrencia.descricao ?? '';
    // A descrição que já cita o documento ("Orçamento nº 12 convertido...") fica como veio.
    const citaDocumento = descricao.toLowerCase().includes(`nº ${ocorrencia.numero}`);

    return {
      icone: ocorrencia.nomeIcone || 'description',
      titulo: ocorrencia.nomeTipoOcorrencia,
      descricao: citaDocumento ? descricao : `${nomeDocumento}: ${descricao}`,
      detalhe: ocorrencia.detalhes,
      quando: ocorrencia.dataOcorrencia,
      quemNome: ocorrencia.nomeUsuario
        || this.autorDoComentario(ocorrencia.idUsuario ?? -1)?.name
        || ocorrencia.nomeOrigemOcorrencia,
      nomeOrigem: ocorrencia.nomeOrigemOcorrencia,
      iconeOrigem: ocorrencia.nomeIconeOrigemOcorrencia,
      exibirOrigem: ocorrencia.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
    };
  }

  /** "Orçamento nº N" ou "Pedido nº N". */
  private nomeDocumentoVendas(idVendaTipoDocumento: number, numero: number | null): string {
    return idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? `Pedido nº ${numero}` : `Orçamento nº ${numero}`;
  }

  /**
   * Junta os registros do CRM da linha do tempo com um registro por documento vinculado (CA-50), por data desc.
   * Documento fora do escopo (Sem acesso) não entra.
   */
  private montarLinhasDoTempo(): void {
    const linhas: LinhaDoTempoFicha[] = [
      ...this.historicoTimeline.map(registro => ({
        chave: `crm-${registro.idOportunidadeOcorrencia}`,
        quando: registro.quando,
        registro,
        documento: null,
      })),
      ...this.documentos.filter(documento => !documento.isSemAcesso).map(documento => ({
        chave: `venda-${documento.idVenda}`,
        quando: documento.dataEmissao,
        registro: null,
        documento,
      })),
    ];
    this.linhasDoTempo = linhas.sort((a, b) => new Date(b.quando).getTime() - new Date(a.quando).getTime());
  }

  /** Título e descrição da linha na aba "linha do tempo", por tipo (protótipo). */
  private textoLinhaDoTempo(
    ocorrencia: OportunidadeOcorrenciaStruct,
    emailAssunto: string | null,
  ): { tituloLinhaDoTempo: string; descricaoLinhaDoTempo: string | null } {
    switch (ocorrencia.idTipoOcorrenciaOportunidade) {
      case TipoOcorrenciaOportunidadeEnum.AcaoPendente:
      case TipoOcorrenciaOportunidadeEnum.AcaoConcluida: {
        const acao = PREFIXO_ACAO.exec((ocorrencia.descricao ?? '').trim());
        return { tituloLinhaDoTempo: acao ? acao[1] : ocorrencia.descricao, descricaoLinhaDoTempo: null };
      }
      case TipoOcorrenciaOportunidadeEnum.EnvioEmail:
        return {
          tituloLinhaDoTempo: ocorrencia.dados ? (emailAssunto ? `E-mail enviado: ${emailAssunto}` : 'E-mail enviado') : ocorrencia.descricao,
          descricaoLinhaDoTempo: ocorrencia.detalhes,
        };
      case TipoOcorrenciaOportunidadeEnum.Anotacao:
        return { tituloLinhaDoTempo: 'Anotação', descricaoLinhaDoTempo: ocorrencia.descricao };
      default:
        return { tituloLinhaDoTempo: ocorrencia.descricao, descricaoLinhaDoTempo: ocorrencia.detalhes };
    }
  }

  /** Mês abreviado da coluna de data da linha do tempo ("OUT"). */
  mesAbreviado(quando: Date | string): string {
    const data = new Date(quando);
    return Number.isNaN(data.getTime()) ? '' : MESES_ABREVIADOS[data.getMonth()];
  }

  corAvatar(nome: string): string {
    return corDoNome(nome);
  }

  iniciaisAvatar(nome: string): string {
    return iniciaisDoNome(nome);
  }

  selecionarAba(aba: AbaFicha): void {
    this.aba = aba;
    if (aba === 'vendas') {
      this.carregarDocumentos();
      this.carregarHistoricoCliente();
    }
    if (aba === 'financeiro') this.carregarFinanceiro();
  }

  /**
   * Na primeira abertura da aba Financeiro; `recarregar` ao ligar/desligar "Incluir pagos recentes" e em
   * "Tentar novamente". Sem Visualizar de Contas a Receber não consulta.
   */
  private carregarFinanceiro(recarregar = false, acao: 'pagosRecentes' | 'tentarNovamente' | null = null): void {
    if (!this.podeVisualizarFinanceiro || this.carregandoFinanceiro
      || ((this.financeiro || this.erroFinanceiro) && !recarregar)) return;
    this.acaoFinanceiro = acao;
    this.carregandoFinanceiro = true;

    this.oportunidadeService.getFinanceiro(this.card.idOportunidade, this.incluirPagosRecentes).subscribe({
      next: response => {
        this.carregandoFinanceiro = false;
        if (response.isError) {
          this.financeiro = null;
          this.erroFinanceiro = response.errorDescription || 'Não foi possível carregar o financeiro do cliente.';
          return;
        }
        // Cada bloco traz o próprio erro (erroTitulos, erroLimite, erroParcelas): a tela mostra no bloco.
        this.financeiro = response;
        this.erroFinanceiro = null;
      },
      error: () => {
        this.carregandoFinanceiro = false;
        this.financeiro = null;
        this.erroFinanceiro =
          'Não foi possível carregar o financeiro do cliente agora. Tente novamente em alguns instantes.';
      },
    });
  }

  alternarPagosRecentes(): void {
    if (this.carregandoFinanceiro) return;
    this.incluirPagosRecentes = !this.incluirPagosRecentes;
    this.carregarFinanceiro(true, 'pagosRecentes');
  }

  /** "Tentar novamente" da aba Financeiro: recarrega só os dados desta aba. */
  tentarFinanceiroNovamente(): void {
    this.carregarFinanceiro(true, 'tentarNovamente');
  }

  /** Situação do título: vencido (só em aberto ou parcial, calculado em Finanças), pago ou a vencer. */
  situacaoTitulo(titulo: TituloClienteStruct): string {
    if (titulo.isVencido) return 'Vencido';
    if (titulo.idStatusContasReceber === StatusContasReceberEnum.Recebido) return 'Pago';
    return 'A vencer';
  }

  classeSituacaoTitulo(titulo: TituloClienteStruct): string {
    if (titulo.isVencido) return 'badge-vermelho';
    if (titulo.idStatusContasReceber === StatusContasReceberEnum.Recebido) return 'badge-cinza';
    return 'badge-verde';
  }

  /** Cores das etapas de Vendas, para a situação dos documentos. */
  private coresKanban: KanbanCor[] = [];

  /** A cor da etapa pelo mesmo resolvedor do Processo de Venda, no tema de agora. */
  corDaEtapa(idKanbanCor: number): PvCorVm {
    return resolverCor(idKanbanCor, this.coresKanban, document.body.classList.contains('dark-mode'));
  }

  /**
   * Uma vez por abertura da ficha; voltar do documento de Vendas reabre a ficha e carrega de novo. `recarregar`
   * depois de vincular ou desvincular.
   */
  private carregarDocumentos(recarregar = false): void {
    if (!this.podeVisualizarVendas || this.carregandoDocumentos || (this.documentosCarregados && !recarregar)) return;
    this.carregandoDocumentos = true;

    // As cores das etapas vêm da configuração do Processo de Venda (em cache no orquestrador), como em Últimas vendas.
    forkJoin({
      response: this.oportunidadeService.getDocumentos(this.card.idOportunidade),
      configuracoes: this.vendaDocumentoService.getConfiguracoes().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ response, configuracoes }) => {
        this.carregandoDocumentos = false;
        this.coresKanban = configuracoes?.isError ? [] : (configuracoes?.kanban?.cores ?? []);
        if (response.isError) {
          this.alertService.show('Aviso',
            response.errorDescription || 'Não foi possível carregar os documentos de Vendas da oportunidade.',
            AlertType.warning);
          return;
        }
        this.documentos = response.documentos ?? [];
        this.documentosCarregados = true;
        this.montarLinhasDoTempo();
      },
      error: () => {
        this.carregandoDocumentos = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar os documentos de Vendas da oportunidade agora. Tente novamente em alguns instantes.',
          AlertType.warning);
      },
    });
  }

  /** Na primeira abertura da aba Vendas; `recarregar` depois de vincular ou desvincular. */
  private carregarHistoricoCliente(recarregar = false): void {
    if (!this.podeVisualizarVendas || this.carregandoHistoricoCliente || (this.historicoCliente && !recarregar)) return;
    this.carregandoHistoricoCliente = true;

    this.oportunidadeService.getHistoricoCliente(this.card.idOportunidade).subscribe({
      next: response => {
        this.carregandoHistoricoCliente = false;
        if (response.isError) {
          this.alertService.show('Aviso',
            response.errorDescription || 'Não foi possível carregar o histórico do cliente.', AlertType.warning);
          return;
        }
        // Cada bloco traz o próprio erro (erroPedidos, erroNotasFiscais, erroOutrosDocumentos): a tela mostra no bloco.
        this.historicoCliente = response;
      },
      error: () => {
        this.carregandoHistoricoCliente = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar o histórico do cliente agora. Tente novamente em alguns instantes.',
          AlertType.warning);
      },
    });
  }

  /** Link do documento de Vendas (abre em outra aba, a ficha continua aberta). */
  urlDocumento(idVenda: number): string {
    return `/vendas/processo-venda/documento/${idVenda}`;
  }

  /** Link da visualização da NF-e de saída. */
  urlNotaFiscal(idNFeSaida: number): string {
    return `/vendas/nota-fiscal-saida/documento/${idNFeSaida}/visualizar`;
  }

  /** Abrir no Processo de Venda (CA-52) e Abrir documento da linha do tempo: só abre, sem conversão. */
  abrirDocumento(documento: VendaDaOportunidadeStruct): void {
    if (documento.isSemAcesso) return;
    window.open(this.urlDocumento(documento.idVenda), '_blank');
  }

  /** Vendedor do documento pelos vendedores da página; "—" sem vendedor. */
  nomeVendedorDocumento(idVendedor: number | null | undefined): string {
    return idVendedor == null ? '—' : nomeDoVendedor(this.vendedoresPorId.get(idVendedor));
  }

  classeStatusNotaFiscal(nota: NotaFiscalSaidaDoDestinatarioStruct): string {
    return classeBadgeStatusNFeSaida(nota.idStatusNFeSaida);
  }

  /** Motivo de Vincular estar desabilitado (oportunidade encerrada, arquivada ou permissão); null quando pode. */
  get motivoVincularBloqueado(): string | null {
    if (this.etapaAtual?.isFinal) return MENSAGEM_VINCULAR_ENCERRADA;
    if (this.card.arquivado) return MENSAGEM_VINCULAR_ARQUIVADA;
    return this.motivoVinculoSemPermissao;
  }

  /** "Desvincular o orçamento N desta oportunidade? ..." — a mesma na aba Vendas e na linha do tempo. */
  textoConfirmacaoDesvinculo(documento: VendaDaOportunidadeStruct): string {
    const tipo = documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'pedido' : 'orçamento';
    return `Desvincular o ${tipo} ${documento.numero} desta oportunidade? O documento continua em Vendas, sem alteração.`;
  }

  /** Ícone do documento na linha do tempo, o mesmo da origem do valor no card. */
  iconeDocumento(documento: VendaDaOportunidadeStruct): string {
    return documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'receipt_long' : 'request_quote';
  }

  /** "Orçamento nº N" ou "Pedido nº N" do documento, para a linha do tempo e os avisos. */
  nomeDocumento(documento: VendaDaOportunidadeStruct): string {
    return this.nomeDocumentoVendas(documento.idVendaTipoDocumento, documento.numero);
  }

  /** O desvincular só abre a confirmação na linha; o "Desvincular" dela é que grava. */
  pedirDesvinculo(documento: VendaDaOportunidadeStruct): void {
    if (this.motivoVinculoSemPermissao || this.idVendaProcessando != null) return;
    this.idVendaConfirmandoDesvinculo = documento.idVenda;
    // A linha de confirmação da tabela depende de um predicado: nova referência faz a tabela reavaliar.
    this.documentos = [...this.documentos];
  }

  cancelarDesvinculo(): void {
    this.idVendaConfirmandoDesvinculo = null;
    this.documentos = [...this.documentos];
  }

  /** Já confirmado na linha. O documento continua em Vendas; o evento fica no Histórico (ocorrência do documento). */
  desvincularDocumento(documento: VendaDaOportunidadeStruct): void {
    if (this.motivoVinculoSemPermissao || this.idVendaProcessando != null) return;
    const nome = this.nomeDocumento(documento);
    this.idVendaProcessando = documento.idVenda;

    this.processoVendaService.desvincularOportunidade(documento.idVenda).subscribe({
      next: response => {
        this.idVendaProcessando = null;
        if (response.isError) {
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível desvincular o documento.', AlertType.warning);
          return;
        }
        this.cancelarDesvinculo();
        this.recarregarAposVinculo();
        this.alertService.show('Documento desvinculado',
          `${nome} continua em Vendas e aparece em "Outros documentos do cliente".`, AlertType.success);
      },
      error: () => {
        this.idVendaProcessando = null;
        this.alertService.show('Aviso',
          'Não foi possível desvincular o documento agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  /** Outros documentos do cliente: vincula direto, sem confirmação. */
  vincularDocumento(documento: VendaDaOportunidadeStruct): void {
    if (this.motivoVincularBloqueado || this.idVendaProcessando != null) return;
    const nome = this.nomeDocumento(documento);
    this.idVendaProcessando = documento.idVenda;

    this.processoVendaService.vincularOportunidade(documento.idVenda, this.card.idOportunidade).subscribe({
      next: response => {
        this.idVendaProcessando = null;
        if (response.isError) {
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível vincular o documento.', AlertType.warning);
          return;
        }
        this.recarregarAposVinculo();
        this.alertService.show('Documento vinculado',
          `${nome} agora faz parte desta oportunidade. O evento ficou no Histórico.`, AlertType.success);
      },
      error: () => {
        this.idVendaProcessando = null;
        this.alertService.show('Aviso',
          'Não foi possível vincular o documento agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  /** Depois de vincular ou desvincular: documentos, linha do tempo, Histórico, histórico do cliente e valor do card. */
  private recarregarAposVinculo(): void {
    this.recarregarHistorico();
    this.carregarDocumentos(true);
    this.carregarHistoricoCliente(true);
    this.recarregarValorDoCard();
  }

  /** O valor é calculado na leitura pelo orquestrador: o documento que define o valor pode ter mudado. */
  private recarregarValorDoCard(): void {
    this.oportunidadeService.getById(this.card.idOportunidade).subscribe({
      next: response => {
        const oportunidade = response.detalhe?.oportunidade;
        if (response.isError || !oportunidade) {
          this.alertService.show('Aviso',
            response.errorDescription || 'Não foi possível atualizar o valor da oportunidade.', AlertType.warning);
          return;
        }
        this.card.valor = oportunidade.valor ?? oportunidade.valorEstimado ?? 0;
        this.card.idVendaValor = oportunidade.idVendaValor ?? null;
        this.card.vendaTipoDocumentoValor = oportunidade.vendaTipoDocumentoValor ?? null;
        this.card.numeroDocumentoValor = oportunidade.numeroDocumentoValor ?? null;
      },
      error: () => this.alertService.show('Aviso',
        'Não foi possível atualizar o valor da oportunidade agora. Tente novamente em alguns instantes.', AlertType.warning),
    });
  }

  /** Com documento vinculado o cliente não muda; antes da lista chegar, vale o documento que define o valor. */
  get clienteBloqueado(): boolean {
    return this.documentosCarregados ? this.documentos.length > 0 : this.card.idVendaValor != null;
  }

  /** Valor definido por documento de Vendas: o campo vira "Valor da oportunidade", sem edição. */
  get valorPorDocumento(): boolean {
    return this.card.idVendaValor != null;
  }

  /** "Valor do orçamento nº X" ou "Valor do pedido nº X"; "Valor estimado" sem documento. */
  get origemValor(): string {
    return origemDoValor(this.card);
  }

  /** Motivo de Criar orçamento e Criar pedido estarem desabilitados; null quando podem ser usados. */
  get motivoCriarDocumentoBloqueado(): string | null {
    if (this.etapaAtual?.isFinal) return MENSAGEM_CRIAR_DOCUMENTO_ENCERRADA;
    if (!this.podeInserirVendas) return MENSAGEM_CRIAR_DOCUMENTO_SEM_PERMISSAO;
    return null;
  }

  criarOrcamento(): void {
    this.criarDocumento(VendaTipoDocumentoEnum.Orcamento);
  }

  criarPedido(): void {
    this.criarDocumento(VendaTipoDocumentoEnum.PedidoDeVenda);
  }

  /** Abre o documento novo já ligado à oportunidade; salvar ou voltar lá traz de volta para esta ficha. */
  private criarDocumento(tipo: VendaTipoDocumentoEnum): void {
    if (this.motivoCriarDocumentoBloqueado) return;

    this.dialogRef.close();
    this.router.navigate(['/vendas/processo-venda/documento'], {
      queryParams: { tipo, idOportunidade: this.card.idOportunidade },
    });
  }

  nomeTipoDocumento(documento: VendaDaOportunidadeStruct): string {
    switch (documento.idVendaTipoDocumento) {
      case VendaTipoDocumentoEnum.PedidoDeVenda: return 'Pedido';
      case VendaTipoDocumentoEnum.Modelo: return 'Modelo';
      default: return 'Orçamento';
    }
  }

  fecharFicha(): void {
    this.fechar(this.card.stage !== this.etapaOriginal ? 'movido' : 'atualizado');
  }

  private fechar(acao: AcaoFichaCard, abrirIdOportunidade?: number): void {
    const resultado: OportunidadeCardDialogResult = {
      acao,
      card: this.card,
      statusCliente: this.statusCliente,
      etapaAnterior: this.etapaOriginal,
      abrirIdOportunidade,
    };
    this.dialogRef.close(resultado);
  }

  private formatarData(data: Date): string {
    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  private textoParaData(texto: string | null | undefined): Date | null {
    if (!texto) return null;
    const [d, m, a] = texto.split('/').map(n => parseInt(n, 10));
    if (!d || !m || !a) return null;
    return new Date(a, m - 1, d);
  }

  /** Lateral (Prospecção em leitura): o valor da oportunidade, do documento que o define ou o estimado. */
  valorLabel(): string {
    return this.card.valor
      ? `R$ ${this.card.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '—';
  }
}
